<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header-left">
          <button class="btn-back" @click="$router.go(-1)">
            <i class="fa fa-arrow-left"></i> Back
          </button>
          <div class="panel-icon"><i class="fa fa-file-invoice-dollar"></i></div>
          <div>
            <div class="panel-title">Create Invoice</div>
            <div class="panel-sub">Select enrollments and generate a client invoice</div>
          </div>
        </div>
        <div class="panel-header-right" v-if="reports.length > 0 && !getLoadingData">
          <button class="btn-generate" @click="createInvoice()" :disabled="isSavingData">
            <i class="fa fa-file-invoice" v-if="!isSavingData"></i>
            <i class="fa fa-spinner fa-spin" v-else></i>
            {{ isSavingData ? 'Generating…' : 'Generate Invoice' }}
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="filter-bar">
        <adv-search
          @search="advSearch"
          :is_date_range="true"
          :is_service="false"
          :is_status="false"
          :is_location_enrollment="false"
          :is_job_report="false"
          :is_admin_sp_location="true"
          :is_admin_sp="currentUser.u.is_admin == 1"
          :is_company="true"
          :is_sp_company="true"
          :is_payment_status="false"
          :reportData="excelreports"
          :titleworksheet="titleworksheet"
          :is_default_tramist_status="'200'"
          :json_fields="json_fields"
          :header="header"
          :report_name="'service_report.xls'"
        />
      </div>

      <!-- Initial prompt -->
      <div class="state-box" v-if="load">
        <i class="fa fa-filter"></i>
        Select a service provider and date range, then click Search
      </div>

      <!-- Loading -->
      <div class="state-box" v-else-if="getLoadingData">
        <i class="fa fa-spinner fa-spin"></i>
        Loading enrollments…
      </div>

      <!-- Empty -->
      <div class="state-box" v-else-if="reports.length === 0">
        <i class="fa fa-inbox"></i>
        No enrollments found for the selected criteria
      </div>

      <!-- Report body -->
      <div class="report-body" v-else>

        <!-- Select All bar -->
        <div class="select-all-bar">
          <label class="check-label">
            <input
              type="checkbox"
              class="check-input"
              v-model="reports.checked"
              @change="handleSelectAllHandler()"
            />
            <span class="check-custom"></span>
            Select All Enrollments
          </label>
          <span class="sel-count">{{ selectedCount }} selected</span>
        </div>

        <!-- SP groups -->
        <div class="sp-group" v-for="(report, i) in reports" :key="i">

          <div class="sp-header">
            <i class="fa fa-building"></i>
            {{ report.sp_name }}
          </div>

          <div class="loc-group" v-for="(loc, j) in report.locations" :key="j">

            <div class="loc-header">
              <i class="fa fa-map-pin"></i>
              {{ loc.location_name }}
            </div>

            <div class="svc-group" v-for="(svc, k) in loc.services" :key="k">

              <div class="svc-header">
                <label class="check-label">
                  <input
                    type="checkbox"
                    class="check-input"
                    v-model="svc.checked"
                    @change="toggleServiceRows(svc)"
                  />
                  <span class="check-custom"></span>
                </label>
                <i class="fa fa-fingerprint"></i>
                {{ svc.service_name }}
              </div>

              <div class="table-wrap">
                <table class="inv-table">
                  <thead>
                    <tr>
                      <th style="width:36px;"></th>
                      <th style="width:36px;">#</th>
                      <th style="min-width:160px;">Applicant / TCN</th>
                      <th v-if="svc.service_type === 'fdle'" style="min-width:90px;">ORI</th>
                      <th style="min-width:110px;">Status</th>
                      <th style="min-width:100px;">Enrolled Date</th>
                      <th class="col-amt">Cost</th>
                      <th class="col-amt">Markup</th>
                      <th class="col-amt">Tax</th>
                      <th class="col-amt">Selling Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, idx) in svc.data"
                      :key="idx"
                      :class="{ 'row-checked': item.checked }"
                    >
                      <td class="col-check">
                        <label class="check-label">
                          <input type="checkbox" class="check-input" v-model="item.checked" />
                          <span class="check-custom"></span>
                        </label>
                      </td>
                      <td class="col-num">{{ idx + 1 }}</td>
                      <td>
                        <div class="applicant-name">{{ item.applicant }}</div>
                        <div class="applicant-tcn" v-if="item.tcn">{{ item.tcn }}</div>
                      </td>
                      <td v-if="svc.service_type === 'fdle'" class="col-mono">{{ item.ori }}</td>
                      <td>
                        <span class="status-chip" :class="statusClass(item.service_status)">
                          {{ mapStatus(item.service_status, svc.service_type) }}
                        </span>
                      </td>
                      <td class="col-date">
                        {{ item.enroll_date ? dateFormat(item.enroll_date) : '—' }}
                      </td>
                      <td class="col-amt">${{ fmt(item.cost) }}</td>
                      <td class="col-amt">${{ fmt(item.markup) }}</td>
                      <td class="col-amt">${{ fmt(item.tax) }}</td>
                      <td class="col-amt">${{ fmt(item.selling_price) }}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="subtotal-row">
                      <td :colspan="svc.service_type === 'fdle' ? 6 : 5" class="subtotal-label">Sub Total</td>
                      <td class="col-amt subtotal-val">${{ fmt(svc.data.reduce((a, e) => a + Number(e.cost), 0)) }}</td>
                      <td class="col-amt subtotal-val">${{ fmt(svc.data.reduce((a, e) => a + Number(e.markup), 0)) }}</td>
                      <td class="col-amt subtotal-val">${{ fmt(svc.data.reduce((a, e) => a + Number(e.tax), 0)) }}</td>
                      <td class="col-amt subtotal-val">${{ fmt(svc.data.reduce((a, e) => a + Number(e.selling_price), 0)) }}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

            </div>
          </div>
        </div>

        <!-- Grand total bar -->
        <div class="grand-total-bar">
          <span class="gt-title">Grand Total (selected)</span>
          <div class="gt-cells">
            <div class="gt-cell">
              <div class="gt-cell-label">Cost</div>
              <div class="gt-cell-val">${{ fmtLocale(grandTotalCost) }}</div>
            </div>
            <div class="gt-cell">
              <div class="gt-cell-label">Markup</div>
              <div class="gt-cell-val">${{ fmtLocale(grandTotalMarkup) }}</div>
            </div>
            <div class="gt-cell">
              <div class="gt-cell-label">Tax</div>
              <div class="gt-cell-val">${{ fmtLocale(grandTotalTax) }}</div>
            </div>
            <div class="gt-cell gt-cell-selling">
              <div class="gt-cell-label">Selling Price</div>
              <div class="gt-cell-val">${{ fmtLocale(grandTotalSelling) }}</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import {
  FETCH_CLIENTS,
  FETCH_SERVICES,
  FETCH_LOCATION_WISE_REPORT,
  GENETRATE_INVOICE_MASTER
} from "@/core/services/store/actions.type";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";
import AdvSearch from "@/includes/search/AdvSearch";

export default {
  components: { AdvSearch },
  computed: {
    ...mapGetters(["currentUser", "getClients", "getServices", "getCompanies", "getSearchDate"]),
    selectedCount() {
      let n = 0;
      this.reports.forEach(r => r.locations.forEach(l => l.services.forEach(s => s.data.forEach(e => { if (e.checked) n++; }))));
      return n;
    },
    grandTotalCost() {
      return this.reports.reduce((a, r) => a + r.locations.reduce((b, l) => b + l.services.reduce((c, s) => c + s.data.reduce((d, e) => e.checked ? d + parseFloat(e.cost || 0) : d, 0), 0), 0), 0);
    },
    grandTotalMarkup() {
      return this.reports.reduce((a, r) => a + r.locations.reduce((b, l) => b + l.services.reduce((c, s) => c + s.data.reduce((d, e) => e.checked ? d + parseFloat(e.markup || 0) : d, 0), 0), 0), 0);
    },
    grandTotalTax() {
      return this.reports.reduce((a, r) => a + r.locations.reduce((b, l) => b + l.services.reduce((c, s) => c + s.data.reduce((d, e) => e.checked ? d + parseFloat(e.tax || 0) : d, 0), 0), 0), 0);
    },
    grandTotalSelling() {
      return this.reports.reduce((a, r) => a + r.locations.reduce((b, l) => b + l.services.reduce((c, s) => c + s.data.reduce((d, e) => e.checked ? d + parseFloat(e.selling_price || 0) : d, 0), 0), 0), 0);
    }
  },
  filters: {
    toFixedTwoDigit(amount) { return parseFloat(amount).toFixed(2); }
  },
  data() {
    return {
      isSavingData: false,
      oldSearchCondition: "",
      oldSearchBillToId: "",
      search: {
        service: "all",
        company: "all",
        date_from: "",
        date_to: "",
        service_status: "all",
        transmit_status: "200",
        payment_status: "all"
      },
      getLoadingData: false,
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Invoice List", route: "admin.client.invoice" },
        { id: 3, title: "Create Invoice", route: "" }
      ],
      reports: [],
      header: [],
      load: true,
      excelreports: [],
      titleworksheet: "",
      json_fields: {
        "": "applicant",
        "  ": "tcn",
        "   ": "ori",
        "    ": "service_status",
        "     ": "enroll_date",
        "      ": "cost",
        "       ": "markup",
        "        ": "tax",
        "         ": "selling_price"
      }
    };
  },
  mounted() {
    this.$store.dispatch(FETCH_CLIENTS);
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.$store.dispatch(FETCH_SERVICES, this.currentUser.u.is_client ? "self" : "all");
  },
  methods: {
    fmt(val) {
      return parseFloat(val || 0).toFixed(2);
    },
    fmtLocale(val) {
      return parseFloat(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    dateFormat(date) {
      if (!date) return "—";
      return new Date(date).toLocaleDateString();
    },
    mapStatus(code, serviceType) {
      if (!code) return "—";
      const isFpc = serviceType === "fingerprintcard";
      if (code === "200" || code === "500") return isFpc ? "Final" : "Ready To Transmit";
      if (code === "300" || code === "600") return isFpc ? "Final" : "Transmitted";
      if (code === "100" || code === "400") return "Draft";
      return code;
    },
    statusClass(code) {
      if (code === "300" || code === "600") return "chip-green";
      if (code === "200" || code === "500") return "chip-blue";
      if (code === "100" || code === "400") return "chip-amber";
      return "chip-neutral";
    },
    toggleServiceRows(svc) {
      svc.data.forEach(e => { e.checked = svc.checked; });
    },
    handleSelectAllHandler() {
      this.reports.forEach(r => r.locations.forEach(l => l.services.forEach(s => {
        s.checked = this.reports.checked;
        s.data.forEach(e => { e.checked = this.reports.checked; });
      })));
    },
    advSearch(value) {
      const { header, search, footer } = value;
      this.header = header;
      this.search = { ...search };

      if (this.currentUser.u.is_admin == 0) {
        if (search.sp_location === "all" && search.company === "all") {
          this.$toastr.e("Please select a location or company");
          return;
        }
      }
      if (search.sp === "all") {
        this.$toastr.e("Please select a service provider first");
        return;
      }
      if (search.sp_location !== "all" && search.company !== "all") {
        this.$toastr.e("Please select either location or company");
        return;
      }

      if (search.sp !== "all") { this.oldSearchCondition = "ISP"; this.oldSearchBillToId = search.sp; }
      if (search.sp_location !== "all") { this.oldSearchCondition = "IL"; this.oldSearchBillToId = search.sp_location; }
      if (search.company !== "all") { this.oldSearchCondition = "IC"; this.oldSearchBillToId = search.company; }

      this.getLoadingData = true;
      this.load = false;
      this.titleworksheet = search.date_from + " - " + search.date_to;
      search.is_Invoice = true;

      this.$store.dispatch(FETCH_LOCATION_WISE_REPORT, search)
        .then(res => {
          this.getLoadingData = false;
          this.reports = res.data;
          this._buildExcelReports(res.data, footer);
        })
        .catch(err => {
          this.getLoadingData = false;
          this.$toastr.e(err.msg || "Error loading data");
        });
    },
    _buildExcelReports(data, footer) {
      let tempArray = [];
      data.forEach(sp => {
        sp.locations.forEach(loc => {
          loc.services.forEach(svc => {
            const totalCost = svc.data.reduce((a, e) => a + Number(e.cost), 0);
            const totalMarkup = svc.data.reduce((a, e) => a + Number(e.markup), 0);
            const totalTax = svc.data.reduce((a, e) => a + Number(e.tax), 0);
            const totalSelling = svc.data.reduce((a, e) => a + Number(e.selling_price), 0);

            tempArray.push(
              { applicant: `<td colspan=7>${sp.sp_name}\n${loc.location_name}\n${svc.service_name}</th>`, ori: "", service_status: "", enroll_date: "", cost: "", markup: "", tax: "", selling_price: "" },
              { applicant: "Applicant Name", tcn: "TCN", ori: "ORI", service_status: "Status", enroll_date: "Enrolled Date", cost: "Cost", markup: "Markup", tax: "Tax", selling_price: "Selling Price" },
              ...svc.data.map(e => {
                e.enroll_date = new Date(e.enroll_date).toLocaleDateString();
                e.service_status = this.mapStatus(e.service_status, e.service_type);
                if (e.service_type !== "fdle") e.ori = "";
                return e;
              }),
              { applicant: "Sub Total", ori: "", service_status: "", enroll_date: "", cost: totalCost, markup: totalMarkup, tax: totalTax, selling_price: totalSelling },
              { applicant: "", ori: "", service_status: "", enroll_date: "", cost: "", markup: "", tax: "", selling_price: "" }
            );
          });
        });
      });

      if (data.length) {
        const gCost = data.reduce((a, r) => a + r.locations.reduce((b, l) => b + l.services.reduce((c, s) => c + s.data.reduce((d, e) => d + parseFloat(e.cost), 0), 0), 0), 0);
        const gMarkup = data.reduce((a, r) => a + r.locations.reduce((b, l) => b + l.services.reduce((c, s) => c + s.data.reduce((d, e) => d + parseFloat(e.markup), 0), 0), 0), 0);
        const gTax = data.reduce((a, r) => a + r.locations.reduce((b, l) => b + l.services.reduce((c, s) => c + s.data.reduce((d, e) => d + parseFloat(e.tax), 0), 0), 0), 0);
        const gSelling = data.reduce((a, r) => a + r.locations.reduce((b, l) => b + l.services.reduce((c, s) => c + s.data.reduce((d, e) => d + parseFloat(e.selling_price), 0), 0), 0), 0);
        tempArray.push({ applicant: "Grand Total", ori: "", service_status: "", enroll_date: "", cost: gCost, markup: gMarkup, tax: gTax, selling_price: gSelling });
      }
      if (footer) {
        tempArray.push({ applicant: "", ori: "", service_status: "", enroll_date: "", cost: footer[0], markup: footer[1], tax: footer[2], selling_price: footer[3] });
      }
      this.excelreports = tempArray;
    },
    createInvoice() {
      const checked = [];
      this.reports.forEach(r => r.locations.forEach(l => l.services.forEach(s => s.data.forEach(e => { if (e.checked) checked.push(e); }))));

      if (checked.length === 0) {
        this.$toastr.e("Please select at least one enrollment to generate the invoice");
        return;
      }

      this.$swal.fire({
        title: "Are you sure?",
        text: `Generate invoice for ${checked.length} enrollment${checked.length !== 1 ? "s" : ""}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "No"
      }).then(result => {
        if (result.isConfirmed) {
          this.isSavingData = true;
          this.$store.dispatch(GENETRATE_INVOICE_MASTER, {
            bill_to: this.oldSearchCondition,
            bill_to_id: this.oldSearchBillToId,
            data: checked
          })
            .then(res => {
              this.isSavingData = false;
              this.$toastr.s(res.msg);
              this.$router.push({ name: "admin.client.invoice" });
            })
            .catch(err => {
              this.isSavingData = false;
              this.$toastr.e(err.msg || "Error generating invoice");
            });
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #dde2ec;

.page-wrap {
  padding: 8px 0 80px;
  font-family: $font;
}

/* ── Panel ── */
.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
  overflow: visible;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #fafbfd;
  border-bottom: 1px solid #eef0f6;
  gap: 12px;
  flex-wrap: wrap;
}

.panel-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.panel-header-right { flex-shrink: 0; }

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 7px;
  border: 1.5px solid $border;
  background: #fff;
  color: #4a5568;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;

  &:hover { background: #f1f5f9; }
}

.panel-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: $blue;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.panel-title {
  font-size: 14px;
  font-weight: 700;
  color: $dark;
}

.panel-sub {
  font-size: 11px;
  color: #8a94a6;
  margin-top: 2px;
}

.btn-generate {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 18px;
  border-radius: 8px;
  border: none;
  background: #16a34a;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s;

  i { font-size: 12px; }

  &:hover:not(:disabled) { background: #15803d; }
  &:disabled { opacity: 0.65; cursor: not-allowed; }
}

.filter-bar {
  padding: 12px 20px;
  border-bottom: 1px solid #f0f3fa;
}

/* ── States ── */
.state-box {
  text-align: center;
  padding: 52px 24px;
  color: #a0aab8;
  font-size: 13px;

  i { font-size: 28px; display: block; margin-bottom: 10px; opacity: 0.4; }
}

/* ── Report body ── */
.report-body { padding: 0 0 4px; }

/* ── Select All bar ── */
.select-all-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #eef0f8;
}

.sel-count {
  font-size: 12px;
  color: #7a8499;
  font-weight: 600;
}

/* ── Checkbox ── */
.check-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: $dark;
  user-select: none;
}

.check-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.check-custom {
  width: 16px;
  height: 16px;
  border: 1.5px solid #b0bac9;
  border-radius: 4px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s, background 0.15s;

  .check-input:checked + & {
    background: $blue;
    border-color: $blue;

    &::after {
      content: '';
      width: 9px;
      height: 5px;
      border-left: 2px solid #fff;
      border-bottom: 2px solid #fff;
      transform: rotate(-45deg) translate(1px, -1px);
      display: block;
    }
  }
}

/* ── SP / Location / Service groups ── */
.sp-group {
  border-bottom: 2px solid #eef0f8;
  &:last-of-type { border-bottom: none; }
}

.sp-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: $dark;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.2px;

  i { font-size: 12px; opacity: 0.7; }
}

.loc-group {
  border-bottom: 1px solid #eef0f8;
  &:last-child { border-bottom: none; }
}

.loc-header {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 20px 7px 32px;
  background: #eef6fc;
  color: #185fa5;
  font-size: 12.5px;
  font-weight: 700;
  border-bottom: 1px solid #d5eaf7;

  i { font-size: 11px; }
}

.svc-group {
  padding: 0 0 8px;
  border-bottom: 1px dashed #eef0f8;
  &:last-child { border-bottom: none; padding-bottom: 0; }
}

.svc-header {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 20px 6px 48px;
  background: #f8fbff;
  color: #276749;
  font-size: 12px;
  font-weight: 700;
  border-bottom: 1px solid #e8f5f0;

  i.fa-fingerprint { font-size: 11px; }
}

/* ── Table ── */
.table-wrap { overflow-x: auto; padding: 0 20px; }

.inv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: $font;
  table-layout: auto;
  margin: 8px 0;

  thead tr {
    border-bottom: 1.5px solid #e2e8f0;

    th {
      padding: 7px 10px;
      font-size: 11px;
      font-weight: 700;
      color: #7a8499;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      white-space: nowrap;
      text-align: left;
      background: #fff;

      &.col-amt { text-align: right; }
    }
  }

  tbody tr {
    border-bottom: 1px solid #f5f7fa;
    transition: background 0.12s;

    &:last-child { border-bottom: none; }
    &:hover td { background: #f8fbff; }
    &.row-checked td { background: #f0f9ff; }

    td { padding: 7px 10px; color: #2d3748; vertical-align: middle; }
  }

  tfoot tr td { padding: 8px 10px; }
}

.col-check { width: 36px; }
.col-num   { color: #a0aab8; font-size: 12px; width: 36px; }
.col-date  { font-size: 12px; color: #6b7280; white-space: nowrap; }
.col-mono  { font-family: 'Courier New', monospace; font-size: 11.5px; color: #4a5568; }
.col-amt   { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }

.applicant-name { font-weight: 500; color: $dark; line-height: 1.3; }
.applicant-tcn  { font-size: 11px; color: #7a8499; font-family: 'Courier New', monospace; margin-top: 1px; }

/* ── Status chips ── */
.status-chip {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.chip-green   { background: #d1fae5; color: #065f46; }
.chip-blue    { background: #dbeafe; color: #1e40af; }
.chip-amber   { background: #fef3c7; color: #92400e; }
.chip-neutral { background: #f1f5f9; color: #64748b; }

/* ── Subtotal row ── */
.subtotal-row td { background: #f8fbff; }

.subtotal-label {
  text-align: right;
  font-weight: 700;
  font-size: 12px;
  color: #4a5568;
}

.subtotal-val {
  font-weight: 700;
  font-size: 12.5px;
  color: $dark;
}

/* ── Grand total bar ── */
.grand-total-bar {
  margin: 0;
  padding: 14px 20px;
  background: $dark;
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  border-radius: 0 0 12px 12px;
}

.gt-title {
  font-size: 12px;
  font-weight: 700;
  color: rgba(255,255,255,0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 8px;
  white-space: nowrap;
}

.gt-cells {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.gt-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.gt-cell-label {
  font-size: 10px;
  color: rgba(255,255,255,0.5);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.gt-cell-val {
  font-size: 14px;
  font-weight: 700;
  color: rgba(255,255,255,0.85);
  font-variant-numeric: tabular-nums;
}

.gt-cell-selling .gt-cell-val {
  color: $blue;
  font-size: 16px;
}
</style>
